import { parseWebglInbound, type WebglInboundMessage, type WebglOutboundMessage } from "@arc/contracts";

/**
 * The Unity ⇄ panel `postMessage` channel (ADR-049 Phase 1, **RED ZONE**).
 *
 * This module is the entire perimeter between a foreign origin and our panel, so it does three things and
 * nothing else: check the origin, validate the payload, hand a typed message to a view-state callback. It
 * imports no mutation hook and no API client — but that is necessary, not sufficient (see {@link WebglBridge}).
 */

/**
 * What the bridge is allowed to do to the panel. **Deliberately a view-state interface** (ADR-049 D2): every
 * inbound message mirrors something Unity has ALREADY persisted, so nothing here needs to write.
 *
 * The enforcement is the shape of this type. A future handler that wants to save something cannot reach a
 * mutation from inside the bridge — it has to widen this interface first, which is the moment a human notices.
 */
export interface WebglViewActions {
  /** Unity selected an element. `selectTab` false = focus its step without activating a tab. */
  selectElement(elementId: number, selectTab: boolean): void;
  /** Unity deleted an element; drop it from the local view only. */
  dropElement(elementId: number): void;
  /** Unity patched a transform; update the local copy so the matrix redraws. */
  patchTransform(elementId: number, t: { position?: string; rotation?: string; size?: string }): void;
  /** Re-read the manual in place (ADR-049 D4's degrade path) — no navigation. */
  refetchBody(): void;
  /** Re-target the panel at another element by rewriting OUR OWN url and reloading. */
  reloadForElement(elementId: number): void;
  /** Drop focus WITHOUT committing a pending draft (ADR-049 D6). */
  blurWithoutCommit(): void;
  /** True when this element is part of the loaded body — the reload/degrade decision needs it. */
  hasElement(elementId: number): boolean;
}

export interface WebglBridgeOptions {
  /** The ONLY origin we accept from and send to. Absent ⇒ the bridge does not arm (ADR-049 D1). */
  peerOrigin: string | undefined;
  actions: WebglViewActions;
  /** Injected for tests; defaults to the real window. */
  win?: Window;
}

/**
 * A live channel. `dispose()` removes the listener; `send()` is a no-op when the bridge never armed.
 *
 * **The invariant, stated as an EFFECT rather than as an import rule** (ADR-049 D2, plan panel): *no inbound
 * message may cause a request to leave the browser.* Phrasing it as "this module imports no mutations" was
 * shown to be beside the point — `blurEditor` could reach a write through the DOM's own blur event, without
 * any import at all. The test for this asserts on the network layer, not on the import graph.
 */
export interface WebglBridge {
  send(message: WebglOutboundMessage): void;
  dispose(): void;
  /** False when no peer origin was configured — the panel is not embedded by a Unity we know. */
  readonly armed: boolean;
}

export function createWebglBridge({ peerOrigin, actions, win = window }: WebglBridgeOptions): WebglBridge {
  // No peer ⇒ no listener at all. Fail closed: a bridge that armed with a permissive default would accept
  // whatever framed us, and "no Unity is configured here" is the normal local-development state.
  if (!peerOrigin) {
    return { send: () => undefined, dispose: () => undefined, armed: false };
  }

  const onMessage = (e: MessageEvent) => {
    // 1. ORIGIN. Exact match, before anything else touches the payload — including before JSON.parse, because
    //    parsing attacker-controlled text is itself work we should not do for a stranger.
    if (e.origin !== peerOrigin) return;

    // 2. SHAPE. Unity sends a JSON *string* (`PostToWebPanel` stringifies); tolerate an object too, because
    //    `postMessage` structured-clones one just as happily and a future build might.
    let raw: unknown = e.data;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        return; // not JSON ⇒ not ours
      }
    }

    const msg = parseWebglInbound(raw);
    if (!msg) return; // unknown type or bad payload — see the contract for why both are quiet

    apply(msg, actions);
  };

  win.addEventListener("message", onMessage);

  return {
    armed: true,
    send(message) {
      // NEVER "*": outbound carries setting values, so a wildcard would post manual content to whatever
      // happens to host the frame. The peer window is the opener (popup/staging) or the parent (iframe/prod).
      // `opener` for the staging popup, `parent` for the production iframe — the same precedence the legacy
      // resolves, minus its `top` fallback, which only mattered for a nesting we do not create.
      const target: Window | null = win.opener !== null && win.opener !== undefined ? (win.opener as Window) : win.parent;
      if (!target || target === win) return; // not embedded — nothing to talk to
      target.postMessage(JSON.stringify(message), peerOrigin);
    },
    dispose() {
      win.removeEventListener("message", onMessage);
    },
  };
}

/** Dispatch. Separated from the listener so a test can drive it without a window. */
function apply(msg: WebglInboundMessage, a: WebglViewActions): void {
  switch (msg.type) {
    case "elementSelected":
      // `selectTab` defaults TRUE — the legacy's `selectTab === undefined ? true : …`, kept because Unity's
      // older messages omit it entirely.
      a.selectElement(msg.elementId, msg.selectTab ?? true);
      return;
    case "elementDeleted":
      a.dropElement(msg.elementId);
      return;
    case "transformChanged":
      a.patchTransform(msg.elementId, { position: msg.position, rotation: msg.rotation, size: msg.size });
      return;
    case "blurEditor":
      a.blurWithoutCommit();
      return;
    case "reloadView":
      // ADR-049 D4, all three cases. A full document reload in someone else's iframe is the most disruptive
      // thing we can do, so it is reserved for the one case that actually needs it: a DIFFERENT element that
      // we can see. `0`/absent is Unity's documented "just refresh", and an id we do not know is stale —
      // both re-read in place instead.
      if (msg.elementId !== undefined && msg.elementId > 0 && a.hasElement(msg.elementId)) {
        a.reloadForElement(msg.elementId);
      } else {
        a.refetchBody();
      }
      return;
  }
}

/** Exported for the tests, which exercise dispatch directly rather than through a window. */
export const __applyForTest = apply;
